import React from 'react';
import {Divider, InputBase, Select, TableCell, TablePaginationProps, Toolbar, Typography, styled} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import TablePaginationActions from './TablePaginationActions';

const RootTableCell = styled(TableCell)(({theme}) => ({
  color: theme.palette.text.primary,
  fontSize: theme.typography.pxToRem(14),
  overflow: 'auto',
  '&:last-child': {
    padding: 0,
  },
}));

const StyledToolbar = styled(Toolbar)(() => ({
  minHeight: 52,
  paddingRight: 2,
  color: 'inherit', // Ensure toolbar inherits parent color
}));

const Spacer = styled('div')({
  flex: '1 1 100%',
});

const CaptionTypography = styled(Typography)({
  flexShrink: 0,
  color: 'inherit',
});

const SelectRootInput = styled(InputBase)(() => ({
  color: 'inherit',
  fontSize: 'inherit',
  flexShrink: 0,
  marginRight: 32,
  marginLeft: 8,
  '& .MuiInputBase-input': {
    color: 'inherit',
  },
}));

const SelectStyled = styled(Select)(() => ({
  paddingLeft: 8,
  paddingRight: 15,
  textAlign: 'right',
  textAlignLast: 'right',
  color: 'inherit',
  '&:before': {
    borderBottomColor: 'currentColor',
    opacity: 0.42,
  },
  '&:hover:not(.Mui-disabled):before': {
    borderBottomColor: 'currentColor',
    opacity: 0.7,
  },
  '&.Mui-focused:after': {
    borderBottomColor: 'currentColor',
  },
  '& .MuiSelect-icon': {
    color: 'inherit', // Changed to inherit parent color
  },
  '& .MuiSelect-select': {
    color: 'inherit',
  },
}));

const ActionsDiv = styled('div')({
  flexShrink: 0,
  marginLeft: 20,
  color: 'inherit', // Pass inherited color to actions
});

const StyledDivider = styled(Divider)(() => ({
  margin: '0 16px',
  backgroundColor: 'currentColor',
  opacity: 0.3,
}));

const defaultLabelDisplayedRows = ({from, to, count}: {from: number; to: number; count: number}) =>
  `${from}-${to} of ${count !== -1 ? count : `more than ${to}`} items`;
const defaultRowsPerPageOptions = [10, 25, 50, 100];

type Props = TablePaginationProps & {
  backIconButtonText?: string;
  nextIconButtonText?: string;
  component?: any;
  onChangePage: (event: React.MouseEvent | null, page: number) => void;
  onChangeRowsPerPage: (event: React.ChangeEvent<{value: unknown}>) => void;
};

const TablePagination = React.forwardRef(function TablePagination(props: Props, ref) {
  const {
    backIconButtonProps,
    backIconButtonText = 'Previous page',
    className,
    colSpan: colSpanProp,
    component: Component = RootTableCell,
    count,
    labelDisplayedRows = defaultLabelDisplayedRows,
    labelRowsPerPage = 'Items per page:',
    nextIconButtonProps,
    nextIconButtonText = 'Next page',
    onChangePage,
    onChangeRowsPerPage,
    page,
    rowsPerPage,
    rowsPerPageOptions = defaultRowsPerPageOptions,
    SelectProps = {},
    ...other
  } = props;
  let colSpan;

  if (Component === RootTableCell || Component === 'td') {
    colSpan = colSpanProp || 1000;
  }

  const MenuItemComponent = SelectProps.native ? 'option' : MenuItem;
  const pagesCount = count === -1 ? 1 : Math.ceil(count / rowsPerPage);

  return (
    <Component className={className} colSpan={colSpan} ref={ref} {...other}>
      <StyledToolbar>
        {rowsPerPageOptions.length > 1 && (
          <CaptionTypography color="inherit" variant="body2">
            {labelRowsPerPage}
          </CaptionTypography>
        )}
        {rowsPerPageOptions.length > 1 && (
          <SelectStyled
            input={<SelectRootInput />}
            value={rowsPerPage}
            // @ts-expect-error: adjusting the type error
            onChange={onChangeRowsPerPage}
            {...SelectProps}
          >
            {rowsPerPageOptions.map((rowsPerPageOption) => (
              <MenuItemComponent
                key={typeof rowsPerPageOption === 'object' ? rowsPerPageOption!.value : rowsPerPageOption}
                value={typeof rowsPerPageOption === 'object' ? rowsPerPageOption!.value : rowsPerPageOption}
              >
                {typeof rowsPerPageOption === 'object' ? rowsPerPageOption.label : rowsPerPageOption}
              </MenuItemComponent>
            ))}
          </SelectStyled>
        )}

        <StyledDivider orientation="vertical" flexItem />

        <CaptionTypography color="inherit" variant="body2">
          {labelDisplayedRows({
            from: count === 0 ? 0 : page * rowsPerPage + 1,
            to: count !== -1 ? Math.min(count, (page + 1) * rowsPerPage) : (page + 1) * rowsPerPage,
            count: count === -1 ? -1 : count,
            page,
          })}
        </CaptionTypography>

        <Spacer />

        {pagesCount > 1 && (
          <>
            <SelectStyled
              input={<SelectRootInput />}
              value={page}
              onChange={(event) => {
                onChangePage(event as any, event.target.value as number);
              }}
            >
              {Array.from(Array(pagesCount).keys()).map((p) => (
                <MenuItemComponent key={p} value={p}>
                  {p + 1}
                </MenuItemComponent>
              ))}
            </SelectStyled>
            <CaptionTypography color="inherit" variant="body2">
              &nbsp;&nbsp;of {pagesCount} pages
            </CaptionTypography>

            <StyledDivider orientation="vertical" flexItem />
          </>
        )}

        <ActionsDiv>
          <TablePaginationActions
            backIconButtonProps={{
              title: backIconButtonText,
              'aria-label': backIconButtonText,
              color: 'inherit',
              ...backIconButtonProps,
            }}
            count={count}
            nextIconButtonProps={{
              title: nextIconButtonText,
              'aria-label': nextIconButtonText,
              color: 'inherit',
              ...nextIconButtonProps,
            }}
            onChangePage={onChangePage}
            page={page}
            rowsPerPage={rowsPerPage}
          />
        </ActionsDiv>
      </StyledToolbar>
    </Component>
  );
});

export default TablePagination;
